import React, { Component } from "react";
import { Col, Row, Input, FormGroup, Spinner } from "reactstrap";
import {
  getTransportSettings,
  updateTransportSettings,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { canWrite } from "../../services/PermissionService";
import { C, Panel, Btn, FieldLabel } from "../../components/Console/ConsoleUI";

/**
 * Transport configuration — the defaults every quote falls back to.
 *
 * A reskin: the same GET and PUT of /transportSettings with the same nine
 * fields and the same client-side range checks before saving.
 *
 * The form only ever offered driver allowance and the minimums; the night-hour
 * and rate-multiplier fields live in state and are round-tripped on save but
 * have never had inputs. They stay that way — surfacing them would change what
 * this screen can write, which is not a reskin — and the panel says so plainly
 * rather than leaving the gap silent.
 */

const CSS = `
.tset input.form-control,.tset select.form-control,.tset .custom-select{height:46px;padding:0 15px;
  background:#fff;border:1px solid #e6d8c4;border-radius:12px;font-size:15px;color:${C.ink};box-shadow:none}
.tset input.form-control:focus,.tset select.form-control:focus{border-color:${C.sand};box-shadow:none}
.tset .form-group{margin-bottom:0}
`;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

class TransportSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      settings: null,
      isLoaded: false,
      isSaving: false,
      hasChanges: false,
      formData: {
        nightStartHour: 20,
        nightEndHour: 6,
        defaultDayRateMultiplier: 1.0,
        defaultNightRateMultiplier: 1.5,
        defaultDriverAllowancePerNight: 50,
        minAdvanceBookingHours: 2,
        defaultMinimumKm: 5,
        defaultMinimumCharge: 10,
        defaultCurrency: "USD",
      },
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Transport", link: "/transport/requests" },
        { title: "Settings", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadSettings();
  }

  loadSettings = () => {
    getTransportSettings()
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          const data = result.data;
          this.setState({
            settings: data,
            formData: {
              nightStartHour: data.nightStartHour || 20,
              nightEndHour: data.nightEndHour || 6,
              defaultDayRateMultiplier: data.defaultDayRateMultiplier || 1.0,
              defaultNightRateMultiplier: data.defaultNightRateMultiplier || 1.5,
              defaultDriverAllowancePerNight: data.defaultDriverAllowancePerNight || 50,
              minAdvanceBookingHours: data.minAdvanceBookingHours || 2,
              defaultMinimumKm: data.defaultMinimumKm || 5,
              defaultMinimumCharge: data.defaultMinimumCharge || 10,
              defaultCurrency: data.defaultCurrency || "USD",
            },
            isLoaded: true,
            hasChanges: false,
          });
        } else {
          this.setState({ isLoaded: true });
          toast.info("No settings found. Default values loaded.");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error loading settings");
      });
  };

  handleChange = (field, value) => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [field]: value,
      },
      hasChanges: true,
    }));
  };

  handleSave = () => {
    const { formData } = this.state;

    // A view-only account cannot save; the server rejects the write anyway, so
    // fail here with a reason rather than letting it look like a server error.
    if (!canWrite()) {
      toast.error("You have view-only access to transport settings");
      return;
    }

    // Validation
    if (formData.nightStartHour < 0 || formData.nightStartHour > 23) {
      toast.error("Night start hour must be between 0 and 23");
      return;
    }
    if (formData.nightEndHour < 0 || formData.nightEndHour > 23) {
      toast.error("Night end hour must be between 0 and 23");
      return;
    }
    if (formData.defaultNightRateMultiplier < 0.1 || formData.defaultNightRateMultiplier > 10) {
      toast.error("Night rate multiplier must be between 0.1 and 10");
      return;
    }
    if (formData.defaultDayRateMultiplier < 0.1 || formData.defaultDayRateMultiplier > 10) {
      toast.error("Day rate multiplier must be between 0.1 and 10");
      return;
    }

    this.setState({ isSaving: true });

    updateTransportSettings(formData)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Settings saved successfully");
          this.setState({ hasChanges: false, settings: response.data });
        } else {
          toast.error(response?.message || "Failed to save settings");
        }
        this.setState({ isSaving: false });
      })
      .catch(() => {
        toast.error("Error saving settings");
        this.setState({ isSaving: false });
      });
  };

  formatHour = (hour) => {
    if (hour === 0) return "12:00 AM (Midnight)";
    if (hour === 12) return "12:00 PM (Noon)";
    if (hour < 12) return `${hour}:00 AM`;
    return `${hour - 12}:00 PM`;
  };

  render() {
    const { isLoaded, isSaving, hasChanges, formData } = this.state;

    return (
      <ConsolePage
        title="Transport settings"
        subtitle="Defaults every quote starts from"
        actions={
          <React.Fragment>
            {hasChanges && (
              <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 14, color: "#b26a00" }}>
                <span className="ms" style={{ fontSize: 19 }}>edit</span>
                Unsaved changes
              </span>
            )}
            <Btn
              kind="quiet"
              icon="arrow_back"
              onClick={() => this.props.history.push("/transport/requests")}
            >
              Requests
            </Btn>
            <Btn icon="check" onClick={this.handleSave} disabled={isSaving || !hasChanges || !canWrite()}>
              {isSaving ? "Saving…" : "Save settings"}
            </Btn>
          </React.Fragment>
        }
      >
        <style>{CSS}</style>

        {!isLoaded ? (
          <Panel>
            <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
              <Spinner style={{ color: C.brown }} />
              <div style={{ marginTop: 10 }}>Loading settings…</div>
            </div>
          </Panel>
        ) : (
          <div className="tset" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>
              <Panel
                title="Driver allowance"
                subtitle="Paid per overnight stay on a multi-day outstation trip"
              >
                <div style={{ marginTop: 18 }}>
                  <FormGroup>
                    <FieldLabel>Allowance per night ($)</FieldLabel>
                    <Input
                      type="number"
                      value={formData.defaultDriverAllowancePerNight}
                      onChange={(e) =>
                        this.handleChange(
                          "defaultDriverAllowancePerNight",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      step="5"
                      min="0"
                    />
                  </FormGroup>
                  <div
                    style={{
                      marginTop: 16, padding: "14px 16px", borderRadius: 12,
                      background: C.iconTint, fontSize: 14, color: C.slate,
                    }}
                  >
                    A three-day trip adds{" "}
                    <strong>{money(formData.defaultDriverAllowancePerNight * 2)}</strong> for the
                    driver — two nights away.
                  </div>
                </div>
              </Panel>

              <Panel
                title="Minimums & booking window"
                subtitle="Applied to per-km services (inter-city, outstation)"
              >
                <div style={{ marginTop: 18 }}>
                  <Row form>
                    <Col md={6}>
                      <FormGroup>
                        <FieldLabel>Minimum distance (km)</FieldLabel>
                        <Input
                          type="number"
                          value={formData.defaultMinimumKm}
                          onChange={(e) =>
                            this.handleChange("defaultMinimumKm", parseInt(e.target.value) || 0)
                          }
                          min="0"
                        />
                        <small style={{ color: C.taupeLight }}>Shorter trips still charge this</small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <FieldLabel>Minimum charge ($)</FieldLabel>
                        <Input
                          type="number"
                          value={formData.defaultMinimumCharge}
                          onChange={(e) =>
                            this.handleChange("defaultMinimumCharge", parseFloat(e.target.value) || 0)
                          }
                          step="5"
                          min="0"
                        />
                        <small style={{ color: C.taupeLight }}>Floor on any trip</small>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row form style={{ marginTop: 16 }}>
                    <Col md={6}>
                      <FormGroup>
                        <FieldLabel>Advance booking (hours)</FieldLabel>
                        <Input
                          type="number"
                          value={formData.minAdvanceBookingHours}
                          onChange={(e) =>
                            this.handleChange("minAdvanceBookingHours", parseInt(e.target.value) || 0)
                          }
                          min="0"
                        />
                        <small style={{ color: C.taupeLight }}>Notice a guest must give</small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <FieldLabel>Default currency</FieldLabel>
                        <Input
                          type="select"
                          value={formData.defaultCurrency}
                          onChange={(e) => this.handleChange("defaultCurrency", e.target.value)}
                        >
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (&euro;)</option>
                          <option value="GBP">GBP (&pound;)</option>
                          <option value="CDF">CDF (FC)</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </Panel>
            </div>

            <Panel title="Night rates" subtitle="Stored on the record, not editable here">
              <div
                style={{
                  marginTop: 16, display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)", gap: 16,
                }}
              >
                {[
                  { label: "Night starts", value: this.formatHour(formData.nightStartHour) },
                  { label: "Night ends", value: this.formatHour(formData.nightEndHour) },
                  { label: "Day multiplier", value: `× ${formData.defaultDayRateMultiplier}` },
                  { label: "Night multiplier", value: `× ${formData.defaultNightRateMultiplier}` },
                ].map((f) => (
                  <div
                    key={f.label}
                    style={{ padding: "13px 15px", borderRadius: 12, background: C.surface }}
                  >
                    <div style={{ fontSize: 12, color: C.taupeLight }}>{f.label}</div>
                    <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>{f.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 14, fontSize: 13, color: C.taupeLight }}>
                These four values are saved back unchanged with everything else on this page. They
                have never had inputs here — change them in the database or in a vehicle type's own
                pricing until the form grows fields for them.
              </div>
            </Panel>

            <Panel>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className="ms" style={{ fontSize: 20, color: C.taupeLight }}>info</span>
                <div style={{ flex: 1, fontSize: 14, color: C.taupe }}>
                  These are defaults. An individual service or vehicle type can override any of them.
                </div>
                <Btn icon="check" onClick={this.handleSave} disabled={isSaving || !hasChanges || !canWrite()}>
                  {isSaving ? "Saving…" : "Save settings"}
                </Btn>
              </div>
            </Panel>
          </div>
        )}
      </ConsolePage>
    );
  }
}

export default TransportSettings;
